import { StatusConfig } from "./journey-types";

/**
 * Maps a fill status string to a StatusConfig for rendering with StatusIndicator.
 *
 * Known mappings:
 * - PURCHASED → success
 * - PENDING → pending
 * - CANCELLED → error
 * - IN_PROGRESS → in-progress
 * - Unknown → info with raw status as label
 */
export function mapFillStatus(status: string): StatusConfig {
  switch (status) {
    case "PURCHASED":
      return { type: "success", label: "Purchased" };
    case "PENDING":
      return { type: "pending", label: "Pending" };
    case "CANCELLED":
      return { type: "error", label: "Cancelled" };
    case "IN_PROGRESS":
      return { type: "in-progress", label: "In Progress" };
    default:
      return { type: "info", label: status };
  }
}

/**
 * Maps a prescription status string to a StatusConfig for rendering with StatusIndicator.
 *
 * Known mappings:
 * - ACTIVE → success
 * - EXPIRED → error
 * - DISCONTINUED → stopped
 * - TRANSFERRED → warning
 * - Unknown → info with raw status as label
 */
export function mapPrescriptionStatus(status: string): StatusConfig {
  switch (status) {
    case "ACTIVE":
      return { type: "success", label: "Active" };
    case "EXPIRED":
      return { type: "error", label: "Expired" };
    case "DISCONTINUED":
      return { type: "stopped", label: "Discontinued" };
    case "TRANSFERRED":
      return { type: "warning", label: "Transferred" };
    default:
      return { type: "info", label: status };
  }
}

/**
 * Maps a shipment condition string (format "code:LABEL") to a StatusConfig.
 * Aligned with backend CustomerJourneyService.mapShipmentCondition().
 *
 * Known mappings:
 * - 8:DELIVERED → success
 * - 7:IN_TRANSIT → in-progress
 * - 6:SHIPPED → in-progress
 * - 5:REFUNDED → warning
 * - 4:CANCELLED → error
 * - 3:AUTHORIZED → pending
 * - 2:PAID → pending
 * - 1:CREATED → pending
 * - Unknown → parses label after ":" or uses raw value, with type info
 */
export function mapShipmentStatus(shipmentCondition: string): StatusConfig {
  switch (shipmentCondition) {
    case "8:DELIVERED":
      return { type: "success", label: "8 - Delivered" };
    case "7:IN_TRANSIT":
      return { type: "in-progress", label: "7 - In Transit" };
    case "6:SHIPPED":
      return { type: "in-progress", label: "6 - Shipped" };
    case "5:REFUNDED":
      return { type: "warning", label: "5 - Refunded" };
    case "4:CANCELLED":
      return { type: "error", label: "4 - Cancelled" };
    case "3:AUTHORIZED":
      return { type: "pending", label: "3 - Authorized" };
    case "2:PAID":
      return { type: "pending", label: "2 - Paid" };
    case "1:CREATED":
      return { type: "pending", label: "1 - Created" };
    default: {
      const colonIndex = shipmentCondition.indexOf(":");
      if (colonIndex >= 0) {
        const code = shipmentCondition.substring(0, colonIndex);
        const rawLabel = shipmentCondition.substring(colonIndex + 1);
        const label = rawLabel
          .split("_")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join(" ");
        return { type: "info", label: `${code} - ${label}` };
      }
      return { type: "info", label: shipmentCondition };
    }
  }
}

/**
 * Maps an order status string (format "code:LABEL") to a StatusConfig.
 * Aligned with backend CustomerJourneyService.mapOrderStatus().
 *
 * Known mappings:
 * - 16:ORDER_PAYMENT_CONFIRMED → success
 * - 4:ORDER_CLOSED → success
 * - 3:ORDER_AUTHORIZED → in-progress
 * - 15:ORDER_HOLDING → in-progress
 * - 7:ORDER_AUTH_PENDING_BANK → in-progress
 * - 20:PENDING_FURTHER_FRAUD_EXAM → in-progress
 * - 1:ORDER_CONFIRMED → pending
 * - 0:ORDER_PENDING → pending
 * - 18:ORDER_GIFT_CLICK_HOLDING → pending
 * - 6:ORDER_CANCELLED → error
 * - 2:ORDER_DECLINED → error
 * - 5:ORDER_SHIPMENT_DECLINED → error
 * - 24:ORDER_HARD_SHIPMENT_DECLINED → error
 * - Unknown → parses label after ":" or uses raw value, with type info
 */
export function mapOrderStatus(orderStatus: string): StatusConfig {
  switch (orderStatus) {
    case "16:ORDER_PAYMENT_CONFIRMED":
      return { type: "success", label: "16 - Confirmed" };
    case "4:ORDER_CLOSED":
      return { type: "success", label: "4 - Order Closed" };
    case "3:ORDER_AUTHORIZED":
      return { type: "in-progress", label: "3 - Authorized" };
    case "15:ORDER_HOLDING":
      return { type: "in-progress", label: "15 - Holding" };
    case "7:ORDER_AUTH_PENDING_BANK":
      return { type: "in-progress", label: "7 - Auth Pending Bank" };
    case "20:PENDING_FURTHER_FRAUD_EXAM":
      return { type: "in-progress", label: "20 - Pending Fraud Exam" };
    case "1:ORDER_CONFIRMED":
      return { type: "pending", label: "1 - Order Confirmed" };
    case "0:ORDER_PENDING":
      return { type: "pending", label: "0 - Order Pending" };
    case "18:ORDER_GIFT_CLICK_HOLDING":
      return { type: "pending", label: "18 - Gift Click Holding" };
    case "6:ORDER_CANCELLED":
      return { type: "error", label: "6 - Cancelled" };
    case "2:ORDER_DECLINED":
      return { type: "error", label: "2 - Declined" };
    case "5:ORDER_SHIPMENT_DECLINED":
      return { type: "error", label: "5 - Shipment Declined" };
    case "24:ORDER_HARD_SHIPMENT_DECLINED":
      return { type: "error", label: "24 - Hard Shipment Declined" };
    default: {
      const colonIndex = orderStatus.indexOf(":");
      if (colonIndex >= 0) {
        const code = orderStatus.substring(0, colonIndex);
        const rawLabel = orderStatus.substring(colonIndex + 1);
        const label = rawLabel
          .split("_")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join(" ");
        return { type: "info", label: `${code} - ${label}` };
      }
      return { type: "info", label: orderStatus };
    }
  }
}
